import type { AutomaticKey, ConfiguredInputVariable, InputActor, InputDefinitions, ParagraphVariable } from "./types";

export type TemplateVariable = {
  variable: ParagraphVariable;
  label: string;
  editorLabel: string;
  group: string;
  sample: string;
};

type AutomaticGroup = {
  label: string;
  values: Array<{ label: string; value: AutomaticKey }>;
};

const entityPrefixes = ["Document", "Resident", "Employee", "Facility", "Family"];

const removeEntityPrefix = (label: string) => {
  const prefix = entityPrefixes.find((entity) => label.toLocaleLowerCase().startsWith(`${entity.toLocaleLowerCase()} - `));
  return prefix ? label.slice(prefix.length + 3) : label;
};

export const withEntityPrefix = (entity: string, label: string) =>
  `${entity} - ${removeEntityPrefix(label)}`;

const automaticGroupDefinitions: AutomaticGroup[] = [
  { label: "Document", values: [{ label: "Title", value: "document.title" }, { label: "Created date", value: "document.createdAt" }, { label: "Finalized date", value: "document.date" }, { label: "Version", value: "document.version" }] },
  { label: "Resident", values: [{ label: "First name", value: "resident.firstName" }, { label: "Last name", value: "resident.lastName" }, { label: "Full name", value: "resident.name" }, { label: "Room", value: "resident.room" }, { label: "Gender", value: "resident.gender" }, { label: "Section", value: "resident.section" }, { label: "Birthdate", value: "resident.birthdate" }, { label: "Admission date", value: "resident.admissionDate" }, { label: "Created date", value: "resident.createdDate" }, { label: "Doctor first name", value: "resident.attendedDoctor.firstName" }, { label: "Doctor last name", value: "resident.attendedDoctor.lastName" }, { label: "Doctor full name", value: "resident.attendedDoctor.name" }] },
  { label: "Employee", values: [{ label: "First name", value: "employee.firstName" }, { label: "Last name", value: "employee.lastName" }, { label: "Full name", value: "employee.name" }, { label: "Email", value: "employee.email" }, { label: "Phone number", value: "employee.phoneNumber" }, { label: "Role", value: "employee.role" }, { label: "Staff ID", value: "employee.staffId" }, { label: "Position", value: "employee.position" }, { label: "Employee type", value: "employee.employeeType" }] },
  { label: "Facility", values: [{ label: "Name", value: "nursingHome.name" }, { label: "Address", value: "nursingHome.address" }, { label: "Phone", value: "nursingHome.phone" }, { label: "Email", value: "nursingHome.email" }, { label: "Facility code", value: "nursingHome.facilityCode" }] },
  { label: "Family", values: [{ label: "First name", value: "family.firstName" }, { label: "Last name", value: "family.lastName" }, { label: "Full name", value: "family.name" }, { label: "Email", value: "family.email" }, { label: "Phone number", value: "family.phoneNumber" }, { label: "Address", value: "family.address" }, { label: "Home number", value: "family.homeNumber" }, { label: "Enduring guardianship", value: "family.enduringGuardianship" }, { label: "Power of attorney", value: "family.powerOfAttorney" }, { label: "Relationship to resident", value: "family.relationship" }, { label: "Contact type", value: "family.contactType" }] },
];

export const automaticGroups = automaticGroupDefinitions.map((group) => ({
  ...group,
  values: group.values.map((item) => ({ ...item, label: withEntityPrefix(group.label, item.label) })),
}));

export const automaticKeys = automaticGroups.flatMap((group) => group.values);
export const automaticKeyValues = automaticKeys.map((item) => item.value);
export const hiddenBuilderAutomaticKeys = new Set<AutomaticKey>([
  "employee.role",
  "employee.staffId",
  "employee.employeeType",
  "nursingHome.facilityCode",
]);

export const isParagraphVariable = (key: string): key is ParagraphVariable =>
  automaticKeys.some((item) => item.value === key)
  || key.startsWith("employee.")
  || key.startsWith("family.");

const sampleOverrides: Partial<Record<AutomaticKey, string>> = {
  "document.title": "Resident vaccination consent",
  "document.date": "2026-09-04",
  "resident.name": "Jane Smith",
  "resident.room": "Room 14B",
  "resident.birthdate": "14 March 1942",
  "resident.attendedDoctor.name": "Dr Priya Patel",
  "employee.name": "Alex Nguyen",
  "employee.email": "alex.nguyen@example.com",
  "nursingHome.name": "Brenna Care Home",
  "family.name": "Morgan Smith",
  "family.phoneNumber": "0400 000 000",
  "family.relationship": "Daughter",
  "family.contactType": "Primary contact",
};

export const sampleValueFor = (key: ParagraphVariable) => {
  const automaticKey = automaticKeys.find((item) => item.value === key)?.value;
  return (automaticKey ? sampleOverrides[automaticKey] : undefined)
    ?? removeEntityPrefix(automaticKeys.find((item) => item.value === key)?.label ?? key);
};

export const allInputs = (definitions: InputDefinitions) =>
  (["employee", "family"] as const).flatMap((actor) =>
    definitions[actor].map((definition) => ({ ...definition, actor })),
  );

const inputVariable = (actor: InputActor, inputId: string): ConfiguredInputVariable =>
  `${actor}.${inputId}`;

export const paragraphVariables = (definitions: InputDefinitions): TemplateVariable[] => {
  const automaticVariables: TemplateVariable[] = automaticGroups.flatMap((group) => group.values
    .filter(({ value }) => !hiddenBuilderAutomaticKeys.has(value))
    .map(({ label, value }) => ({
      variable: value,
      label,
      editorLabel: removeEntityPrefix(label),
      group: group.label,
      sample: sampleValueFor(value),
    })));
  const inputVariables: TemplateVariable[] = allInputs(definitions).map((input) => ({
    variable: inputVariable(input.actor, input.id),
    label: withEntityPrefix(input.actor === "employee" ? "Employee" : "Family", input.label),
    editorLabel: removeEntityPrefix(input.label),
    group: input.actor === "employee" ? "Employee form inputs" : "Family form inputs",
    sample: input.type === "checkbox" ? "Yes" : input.type === "date" ? "19/10/2026" : input.type === "datetime" ? "19/10/2026 2:30 PM" : input.options?.[0]?.label ?? `Sample ${input.label.toLowerCase()}`,
  }));
  return [...automaticVariables, ...inputVariables];
};

export const findInputDefinition = (definitions: InputDefinitions, actor: InputActor, inputId: string) =>
  definitions[actor].find((definition) => definition.id === inputId);
